import type { GroupResponse } from "@ccflare/api";
import { AlertCircle, Edit2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import {
	useCreateGroup,
	useDeleteGroup,
	useGroups,
	useUpdateGroup,
} from "../hooks/queries";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

function formatDate(iso: string): string {
	return new Date(iso).toLocaleString();
}

// ---- Create Dialog --------------------------------------------------------

interface CreateGroupDialogProps {
	onClose: () => void;
}

function CreateGroupDialog({ onClose }: CreateGroupDialogProps) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [error, setError] = useState<string | null>(null);
	const create = useCreateGroup();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		try {
			await create.mutateAsync({
				name: name.trim(),
				description: description.trim() || undefined,
			});
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to create group");
		}
	};

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create Routing Group</DialogTitle>
					<DialogDescription>
						Groups let you route requests to specific sets of accounts.
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="group-name">Name</Label>
						<Input
							id="group-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g. production"
							required
							autoFocus
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="group-description">Description (optional)</Label>
						<Input
							id="group-description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="e.g. Production accounts"
						/>
					</div>
					{error && (
						<p className="text-sm text-destructive flex items-center gap-1">
							<AlertCircle className="h-4 w-4" />
							{error}
						</p>
					)}
					<DialogFooter>
						<Button type="button" variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" disabled={create.isPending || !name.trim()}>
							{create.isPending ? "Creating..." : "Create"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

// ---- Edit Dialog ----------------------------------------------------------

interface EditGroupDialogProps {
	group: GroupResponse;
	onClose: () => void;
}

function EditGroupDialog({ group, onClose }: EditGroupDialogProps) {
	const [name, setName] = useState(group.name);
	const [description, setDescription] = useState(group.description ?? "");
	const [error, setError] = useState<string | null>(null);
	const update = useUpdateGroup();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		try {
			await update.mutateAsync({
				id: group.id,
				data: {
					name: name.trim(),
					description: description.trim() || undefined,
				},
			});
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to update group");
		}
	};

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Edit Group</DialogTitle>
					<DialogDescription>Update the name or description.</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="edit-group-name">Name</Label>
						<Input
							id="edit-group-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							autoFocus
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="edit-group-description">
							Description (optional)
						</Label>
						<Input
							id="edit-group-description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="Leave blank to clear"
						/>
					</div>
					{error && (
						<p className="text-sm text-destructive flex items-center gap-1">
							<AlertCircle className="h-4 w-4" />
							{error}
						</p>
					)}
					<DialogFooter>
						<Button type="button" variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" disabled={update.isPending || !name.trim()}>
							{update.isPending ? "Saving..." : "Save"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

// ---- Delete Confirm Dialog ------------------------------------------------

interface DeleteGroupDialogProps {
	group: GroupResponse;
	onConfirm: () => void;
	onCancel: () => void;
	loading: boolean;
	serverError: string | null;
}

function DeleteGroupDialog({
	group,
	onConfirm,
	onCancel,
	loading,
	serverError,
}: DeleteGroupDialogProps) {
	const canDelete =
		!group.isDefault && group.accountCount + group.apiKeyCount === 0;

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Delete Group</DialogTitle>
					<DialogDescription>
						Permanently delete group <strong>{group.name}</strong>?
					</DialogDescription>
				</DialogHeader>
				{!canDelete && (
					<div className="rounded-md border border-warning bg-warning/10 p-3 text-sm text-warning-foreground">
						{group.isDefault
							? "The default group cannot be deleted."
							: "Move its accounts and keys to another group first."}
					</div>
				)}
				{serverError && (
					<p className="text-sm text-destructive flex items-center gap-1">
						<AlertCircle className="h-4 w-4" />
						{serverError}
					</p>
				)}
				<DialogFooter>
					<Button variant="outline" onClick={onCancel} disabled={loading}>
						Cancel
					</Button>
					<Button
						variant="destructive"
						onClick={onConfirm}
						disabled={loading || !canDelete}
					>
						{loading ? "Deleting..." : "Delete"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---- Main Tab -------------------------------------------------------------

export function GroupsTab() {
	const { data: groups, isLoading } = useGroups();
	const deleteGroup = useDeleteGroup();

	const [showCreate, setShowCreate] = useState(false);
	const [editTarget, setEditTarget] = useState<GroupResponse | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<GroupResponse | null>(null);
	const [deleteError, setDeleteError] = useState<string | null>(null);

	const handleDelete = async () => {
		if (!deleteTarget) return;
		setDeleteError(null);
		try {
			await deleteGroup.mutateAsync({ id: deleteTarget.id });
			setDeleteTarget(null);
		} catch (err) {
			setDeleteError(
				err instanceof Error ? err.message : "Failed to delete group",
			);
		}
	};

	if (isLoading) {
		return (
			<Card>
				<CardContent className="pt-6">
					<p className="text-muted-foreground">Loading groups...</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-4">
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>Routing Groups</CardTitle>
							<CardDescription>
								Accounts and API keys belong to a group; requests are routed
								only to accounts in the key's group.
							</CardDescription>
						</div>
						<Button onClick={() => setShowCreate(true)} size="sm">
							<Plus className="mr-2 h-4 w-4" />
							Create Group
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{!groups?.length ? (
						<p className="text-sm text-muted-foreground py-4 text-center">
							No groups found.
						</p>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b text-muted-foreground text-left">
										<th className="pb-2 pr-4 font-medium">Name</th>
										<th className="pb-2 pr-4 font-medium">Description</th>
										<th className="pb-2 pr-4 font-medium">Accounts</th>
										<th className="pb-2 pr-4 font-medium">Keys</th>
										<th className="pb-2 pr-4 font-medium">Created</th>
										<th className="pb-2 font-medium" />
									</tr>
								</thead>
								<tbody className="divide-y divide-border">
									{groups.map((group) => (
										<tr key={group.id} className="hover:bg-muted/30">
											<td className="py-3 pr-4 font-medium">
												<span className="flex items-center gap-2">
													{group.name}
													{group.isDefault && (
														<Badge variant="secondary" className="text-xs">
															Default
														</Badge>
													)}
												</span>
											</td>
											<td className="py-3 pr-4 text-muted-foreground">
												{group.description ?? "—"}
											</td>
											<td className="py-3 pr-4">
												{group.accountCount.toLocaleString()}
											</td>
											<td className="py-3 pr-4">
												{group.apiKeyCount.toLocaleString()}
											</td>
											<td className="py-3 pr-4 text-muted-foreground text-xs">
												{formatDate(group.createdAt)}
											</td>
											<td className="py-3">
												<div className="flex items-center gap-1">
													<Button
														variant="ghost"
														size="sm"
														onClick={() => setEditTarget(group)}
														title="Edit group"
													>
														<Edit2 className="h-4 w-4" />
													</Button>
													<Button
														variant="ghost"
														size="sm"
														className="text-destructive hover:text-destructive"
														onClick={() => {
															setDeleteError(null);
															setDeleteTarget(group);
														}}
														title={
															group.isDefault
																? "Cannot delete the default group"
																: group.accountCount + group.apiKeyCount > 0
																	? "Move its accounts and keys first"
																	: "Delete group"
														}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			{showCreate && <CreateGroupDialog onClose={() => setShowCreate(false)} />}

			{editTarget && (
				<EditGroupDialog
					group={editTarget}
					onClose={() => setEditTarget(null)}
				/>
			)}

			{deleteTarget && (
				<DeleteGroupDialog
					group={deleteTarget}
					onConfirm={() => void handleDelete()}
					onCancel={() => {
						setDeleteTarget(null);
						setDeleteError(null);
					}}
					loading={deleteGroup.isPending}
					serverError={deleteError}
				/>
			)}
		</div>
	);
}
